import React, { useState, useEffect, useRef } from "react";

import "../css/NavTableCell.css";

import { formatField } from "../../js/format";
import { formatCurrency, formatFloatFromCurrency } from "../utils/utils";

import TooltipBox from "../../components/Tooltip";

export default ({
  id,
  value,
  type,
  canBeEmpty,
  onChange,
  onNoNextCell,
  showList,
  updateList,
  onSelectOption,
  onPressEnter,
  onBlur,
  readOnly,
  justify = "left",
  stock,
  maxLength,
}) => {
  const [inputType, setInputType] = useState("type-alphanum-ext");
  const [currentValue, setCurrentValue] = useState(
    value.toString().toUpperCase()
  );

  const [currentFormattedValue, setCurrentFormattedValue] = useState(
    value.toString().toUpperCase()
  );
  const [optionList, setOptionList] = useState([]);
  const [selectedOp, setSelectedOp] = useState(-1);
  const [focused, setFocused] = useState(false);

  const inputRef = useRef(null);
  const navtableRef = useRef(null);
  const valueAtFocusRef = useRef(currentValue);

  const shouldTriggerSearch = () =>
    Boolean(onPressEnter) &&
    currentValue.toString().trim() !== "" &&
    currentValue !== valueAtFocusRef.current;

  const triggerSearch = (v) => {
    // Se actualiza la referencia antes de lanzar la búsqueda para que, si
    // el propio onPressEnter mueve el foco (y por tanto dispara el blur de
    // esta celda), no se vuelva a considerar el código como "cambiado" y
    // se repita la búsqueda una segunda vez.
    valueAtFocusRef.current = v;
    onPressEnter(v);
  };

  const focusAndSelect = (el) => {
    el.focus();
    setTimeout(() => {
      // Si el foco ya se movió a otra celda (navegación rápida posterior),
      // no reclamarlo de vuelta.
      if (document.activeElement === el) {
        el.select();
      }
    }, 1);
  };

  const selectNextCell = () => {
    const tokens = id.split("-");
    const col = tokens[2];
    if (col < 8) {
      // Next column
      const newId = tokens[0] + "-" + tokens[1] + "-" + (parseInt(col) + 1);
      const el = document.getElementById(newId);
      if (el) {
        focusAndSelect(el);
      }
    } else {
      // Next row
      const newId = tokens[0] + "-" + (parseInt(tokens[1]) + 1) + "-1";
      const el = document.getElementById(newId);
      if (el) {
        focusAndSelect(el);
      } else {
        // No more rows
        if (onNoNextCell) {
          onNoNextCell();
        }
      }
    }
  };

  const scrollToItem = (text) => {
    const children = navtableRef.current.querySelectorAll("div.dropdown-op");
    if (children) {
      const childrenArr = Array.from(children);
      const child = childrenArr.find((item) => {
        let found = false;
        const span = item.querySelector("span");
        if (span) {
          if (span.textContent === text) {
            found = true;
          }
        }
        return found;
      });
      if (child) {
        child.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.repeat) {
      // Ignorar repeticiones automáticas al mantener pulsada una tecla,
      // ya que la navegación puede disparar llamadas async (p.ej. lectura
      // de producto) y provocar saltos de foco descontrolados.
      return;
    }

    let tokens = null;
    let line = "";
    let col = "";
    let newId = "";
    let el = null;
    let pos = -1;
    let sel = -1;
    switch (e.key) {
      case "ArrowDown":
        if (showList && optionList.length > 0) {
          // Dropdown open
          sel = selectedOp + 1;
          if (sel < optionList.length) {
            setSelectedOp(sel);
            scrollToItem(optionList[sel].text);
          }
        } else {
          // Dropdown closed, next row
          tokens = id.split("-");
          line = tokens[1];
          newId = tokens[0] + "-" + (parseInt(line) + 1) + "-" + tokens[2];
          el = document.getElementById(newId);
          if (el) {
            focusAndSelect(el);
          } else {
            if (onNoNextCell) {
              onNoNextCell();
            }
          }
        }
        break;
      case "ArrowUp":
        if (showList && optionList.length > 0) {
          // Dropdown open
          sel = selectedOp - 1;
          if (sel >= 0) {
            setSelectedOp(sel);
            scrollToItem(optionList[sel].text);
          }
        } else {
          // Dropdown closed, next row
          tokens = id.split("-");
          line = tokens[1];
          newId = tokens[0] + "-" + (parseInt(line) - 1) + "-" + tokens[2];
          el = document.getElementById(newId);
          if (el) {
            focusAndSelect(el);
          }
        }
        break;
      case "ArrowRight":
        if (
          inputRef.current.selectionStart === currentValue.toString().length
        ) {
          if (shouldTriggerSearch()) {
            triggerSearch(currentValue);
          } else {
            selectNextCell();
          }
        }
        break;
      case "ArrowLeft":
        if (
          inputRef.current.selectionStart === 0 &&
          inputRef.current.selectionEnd === 0
        ) {
          tokens = id.split("-");
          col = tokens[2];
          if (col < 9) {
            newId = tokens[0] + "-" + tokens[1] + "-" + (parseInt(col) - 1);
            el = document.getElementById(newId);
            if (el) {
              focusAndSelect(el);
            }
          }
        }
        break;
      case "Tab":
        if (shouldTriggerSearch()) {
          triggerSearch(currentValue);
        }
        break;
      case "Enter":
        if (onPressEnter) {
          if (shouldTriggerSearch()) {
            triggerSearch(currentValue);
          } else {
            selectNextCell();
          }
        } else {
          if (showList && optionList.length > 0) {
            // Dropdown open
            if (selectedOp >= 0) {
              selectOption(optionList[selectedOp]);
            }
          } else {
            // Dropdown closed, next row
            tokens = id.split("-");
            if (tokens[2] === "1") {
              // Code
              line = tokens[1];
              newId = tokens[0] + "-" + (parseInt(line) + 1) + "-" + tokens[2];
              el = document.getElementById(newId);
              if (el) {
                focusAndSelect(el);
              } else {
                if (onNoNextCell) {
                  onNoNextCell();
                }
              }
            } else {
              selectNextCell();
            }
          }
        }
        break;
    }
  };

  const onChangeValue = async (e) => {
    const v = formatField(
      inputRef.current,
      e.target.value.toUpperCase(),
      inputType,
      true
    );
    setCurrentValue(v);
    setCurrentFormattedValue(v);
    onChange(v);

    if (showList) {
      const list = await updateList(v);
      setOptionList(list);
      setSelectedOp(-1);
    }
  };

  const onUnfocus = () => {
    let cValue = currentValue;
    if (!canBeEmpty) {
      if (currentValue === "") {
        cValue = "0";
      }
    }
    const fv = formatValue(cValue, inputType);
    setCurrentValue(cValue);
    setCurrentFormattedValue(fv);
    if (showList) {
      setTimeout(() => {
        setOptionList([]);
        setSelectedOp(-1);
      }, 100);
    }
    setFocused(false);
    if (shouldTriggerSearch()) {
      triggerSearch(cValue);
    } else if (onBlur) {
      onBlur(cValue);
    }
  };

  const onFocus = () => {
    const v = unformatValue(currentFormattedValue, inputType);
    setCurrentValue(v);
    setCurrentFormattedValue(v);
    valueAtFocusRef.current = v;
    setTimeout(() => {
      if (document.activeElement === inputRef.current) {
        inputRef.current.select();
      }
    }, 1);
    setFocused(true);
  };

  const formatValue = (v, iType) => {
    let fv = v;
    switch (type) {
      case "type-int-perc":
        fv = v + " %";
        break;
      case "type-float-perc":
        fv = v + " %";
        break;
      case "type-currency":
        /*fv = reformatField(inputRef.current, v, iType, false);
        const tokens = fv.split(".");
        let intN = tokens[0];
        const decN = tokens[1] ? tokens[1] : "00";
        intN = intN.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        fv = intN + "." + decN;
        fv = fv + " €";*/
        fv = formatCurrency(v);
        break;
    }
    return fv;
  };

  const unformatValue = (fv, iType) => {
    let v = fv;
    switch (type) {
      case "type-int-perc":
        v = v.replace(/\%/g, "");
        v = parseInt(v);
        break;
      case "type-float-perc":
        v = v.replace(/\%/g, "");
        v = parseFloat(v);
        break;
      case "type-currency":
        v = formatFloatFromCurrency(v);
        break;
    }
    return v;
  };

  const selectOption = (option) => {
    onSelectOption(option.element);

    const v = formatField(
      inputRef.current,
      option.text.toUpperCase(),
      inputType,
      true
    );
    setCurrentValue(v);
    setCurrentFormattedValue(v);

    setOptionList([]);
    setSelectedOp(-1);
  };

  useEffect(() => {
    let iType = type;
    switch (type) {
      case "type-int-perc":
        iType = "type-only-numbers";
        break;
      case "type-float-perc":
        iType = "type-float";
        break;
      case "type-currency":
        iType = "type-float2";
        break;
    }
    setInputType(iType);
    setCurrentFormattedValue(formatValue(currentValue, iType));
  }, []);

  useEffect(() => {
    setCurrentValue(value.toString().toUpperCase());
    setCurrentFormattedValue(
      focused
        ? value.toString().toUpperCase()
        : formatValue(value.toString().toUpperCase(), inputType)
    );
    // Se incluye "focused" para que, al perder el foco, la celda se
    // resincronice siempre con el valor real recibido por props — aunque
    // el padre haya recortado/corregido el valor tecleado a uno que
    // coincide con el que ya tenía antes (p. ej. un PVU que se clampa de
    // vuelta al PVP), ya que en ese caso "value" no cambia y este efecto
    // no se dispararía solo por su propia dependencia.
  }, [value, focused]);

  return (
    <div
      className={
        "navtablecell " +
        justify +
        (stock !== "" && stock <= 0 ? " no-stock" : "")
      }
      ref={navtableRef}
      //title={stock !== "" ? "Stock: " + stock : null}
      data-tooltip-id={"tts-" + id}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={currentFormattedValue}
        onKeyDown={onKeyDown}
        onChange={onChangeValue}
        onFocus={readOnly ? null : () => onFocus()}
        onBlur={readOnly ? null : () => onUnfocus()}
        style={readOnly ? { pointerEvents: "none" } : null}
        autoComplete="off"
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : null}
        maxLength={maxLength}
      />
      {showList && optionList.length > 0 && (
        <div className="dropdown-options">
          {optionList.map((item, i) => {
            return (
              <div
                className="dropdown-op"
                key={i}
                onClick={(e) => {
                  selectOption(item);
                  e.stopPropagation();
                }}
                onTouchEnd={(e) => {
                  selectOption(item);
                  e.stopPropagation();
                  e.preventDefault();
                }}
              >
                <span className={selectedOp === i ? "selected-op" : ""}>
                  {item.text}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {stock !== "" && (
        <TooltipBox
          id={"tts-" + id}
          content={<div className="cell">Stock: {stock}</div>}
        />
      )}
    </div>
  );
};
